import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { api, mediaSrcById } from "../api";
import { resizeToSquareJpeg } from "../lib/imageResize";
import { AvatarCropDialog } from "../components/AvatarCropDialog";
import { AvatarLightbox } from "../components/AvatarLightbox";
import { describePasskeyError, reportPasskeyError } from "../lib/passkeyDebug";
import { useApp } from "../context/AppContext";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "../components/ui/input-otp";
import { startRegistration } from "@simplewebauthn/browser";
import { toast } from "sonner";
import { ArrowLeft, KeyRound, Fingerprint, LogOut, Loader2, Mail, Trash2, MailCheck, Camera, Building2 } from "lucide-react";
import { PersonalConnectBlock } from "../components/PersonalConnect";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "../components/ui/alert-dialog";

function initials(name) {
  return (name || "").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
}

/** Slim profile card: my company (with "request leave") + PERSONAL connections
 *  (QR / connect code). Company business cards + partnerships now live in
 *  Admin → Business Cards. */
const PersonalConnectionsCard = ({ t }) => {
  const { user } = useApp();
  const [company, setCompany] = useState(null);
  const [canLeave, setCanLeave] = useState(true);
  const [leavePending, setLeavePending] = useState(false);
  // one-step Admin handover (shown when I'm the ONLY admin)
  const [transferOpen, setTransferOpen] = useState(false);
  const [members, setMembers] = useState([]);
  const [transferBusy, setTransferBusy] = useState(null);

  useEffect(() => {
    api.get("/company/mine").then((r) => {
      setCompany(r.data.company);
      setCanLeave(r.data.company?.can_leave !== false);
    }).catch(() => {});
  }, []);

  const openTransfer = async () => {
    setTransferOpen(true);
    try {
      const { data } = await api.get("/users");
      setMembers((data || []).filter((u) => u.id !== user?.id && u.company_id === company?.id));
    } catch (e) { setMembers([]); }
  };

  const transferTo = async (m) => {
    setTransferBusy(m.id);
    try {
      await api.post("/company/transfer-admin", { user_id: m.id });
      toast.success(`${m.name} ${t("transfer_admin_done")}`);
      setTransferOpen(false);
      setCanLeave(true);
    } catch (e) {
      toast.error(t("something_wrong"));
    } finally { setTransferBusy(null); }
  };

  const requestLeave = async (purge) => {
    try {
      await api.post("/company/leave", { purge });
      setLeavePending(true);
      toast.success(t("leave_requested_toast"));
    } catch (e) {
      const detail = e?.response?.data?.detail || "";
      if (detail === "last_admin") toast.error(t("last_admin_error"));
      else toast.error(t("something_wrong"));
    }
  };

  return (
    <Card className="p-5 mb-5" data-testid="profile-connect-card">
      <h2 className="font-display font-semibold text-base flex items-center gap-2 mb-1">
        <Building2 className="h-4 w-4 text-primary" /> {t("personal_connections_title")}
      </h2>
      <p className="text-xs text-muted-foreground mb-3" data-testid="profile-company-name">
        {company ? `${t("company")}: ${company.name}` : t("no_company")}
      </p>
      {/* Only-admin companies: the single Admin can never leave (invariant) —
          offer the one-step handover instead */}
      {company && !canLeave && (
        <div className="mb-3 rounded-lg border bg-muted/40 p-3">
          <p className="text-[11px] text-muted-foreground mb-2">{t("transfer_admin_hint")}</p>
          <Button type="button" variant="outline" size="sm" className="h-9" data-testid="transfer-admin-btn" onClick={openTransfer}>
            <KeyRound className="h-3.5 w-3.5 mr-1.5" /> {t("transfer_admin")}
          </Button>
        </div>
      )}
      <Dialog open={transferOpen} onOpenChange={(v) => { if (!v) setTransferOpen(false); }}>
        <DialogContent className="w-[calc(100vw-2rem)] max-w-sm rounded-2xl" data-testid="transfer-admin-dialog">
          <DialogHeader>
            <DialogTitle className="text-base">{t("transfer_admin_pick")}</DialogTitle>
          </DialogHeader>
          <div className="max-h-[45vh] overflow-y-auto space-y-1">
            {members.length === 0 && <p className="text-sm text-muted-foreground text-center py-4">—</p>}
            {members.map((m) => (
              <button
                key={m.id}
                type="button"
                data-testid={`transfer-admin-pick-${m.id}`}
                disabled={!!transferBusy}
                onClick={() => transferTo(m)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border hover:bg-muted/60 transition-colors text-left disabled:opacity-60"
              >
                <div
                  className="h-9 w-9 rounded-full flex items-center justify-center text-white text-xs font-semibold shrink-0"
                  style={{ backgroundColor: m.avatar_color || "#64748b" }}
                >
                  {(m.name || "?").split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase()}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate">{m.name}</div>
                  <div className="text-[11px] text-muted-foreground truncate">{m.title || t(m.role)}</div>
                </div>
                {transferBusy === m.id && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
              </button>
            ))}
          </div>
        </DialogContent>
      </Dialog>
      {/* Only-admin companies: the single Admin can never leave (invariant) */}
      {company && canLeave && (
        <div className="mb-3">
          {leavePending ? (
            <p className="text-[11px] text-amber-600" data-testid="leave-pending-note">{t("leave_pending_note")}</p>
          ) : (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <button type="button" className="text-[11px] text-destructive underline underline-offset-2" data-testid="request-leave-company-btn">
                  {t("request_leave_company")}
                </button>
              </AlertDialogTrigger>
              <AlertDialogContent data-testid="leave-company-dialog">
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("request_leave_company")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("leave_company_confirm")}
                    <span className="block mt-2 font-medium text-foreground">{t("purge_related_question")}</span>
                    <span className="block mt-1 text-[11px]">{t("purge_no_hint")}</span>
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter className="flex-col sm:flex-col gap-2">
                  <AlertDialogAction
                    data-testid="leave-confirm-purge-btn"
                    className="w-full bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    onClick={() => requestLeave(true)}
                  >
                    {t("purge_yes_delete_all")}
                  </AlertDialogAction>
                  <AlertDialogAction
                    data-testid="leave-confirm-btn"
                    className="w-full"
                    onClick={() => requestLeave(false)}
                  >
                    {t("purge_no_keep_loose")}
                  </AlertDialogAction>
                  <AlertDialogCancel data-testid="leave-cancel-btn" className="w-full mt-0">{t("cancel")}</AlertDialogCancel>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
      )}
      <PersonalConnectBlock />
    </Card>
  );
};

export default function ProfilePage() {
  const { user, setUser, logout, t } = useApp();
  const navigate = useNavigate();
  const avatarFileRef = useRef(null);
  const [avatarBusy, setAvatarBusy] = useState(false);
  const [avatarLightbox, setAvatarLightbox] = useState(false);
  const [cropFile, setCropFile] = useState(null); // picked file → circular crop dialog

  const onPickAvatar = (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file || !user) return;
    setCropFile(file); // open the crop dialog — upload happens after confirm
  };

  const uploadCroppedAvatar = async (b64) => {
    setCropFile(null);
    setAvatarBusy(true);
    try {
      const res = await api.post("/media", {
        data_base64: b64, content_type: "image/jpeg", kind: "image",
        category: "avatar", uploaded_by: user.id, filename: "avatar.jpg",
      });
      await api.patch(`/users/${user.id}`, { avatar_media_id: res.data.id });
      setUser({ ...user, avatar_media_id: res.data.id });
      toast.success(t("photo_updated"));
    } catch (err) {
      toast.error(t("something_wrong"));
    } finally {
      setAvatarBusy(false);
    }
  };

  const removeAvatar = async () => {
    try {
      await api.patch(`/users/${user.id}`, { avatar_media_id: "" });
      setUser({ ...user, avatar_media_id: null });
      toast.success(t("photo_removed"));
    } catch (err) {
      toast.error(t("something_wrong"));
    }
  };

  const [current, setCurrent] = useState("");
  const [newPw, setNewPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  // "Forgot current password" mode — verify with an emailed 6-digit code instead
  const [codeMode, setCodeMode] = useState(false);
  const [code, setCode] = useState("");
  const [codeCooldown, setCodeCooldown] = useState(0);

  useEffect(() => {
    if (codeCooldown <= 0) return;
    const iv = setInterval(() => setCodeCooldown((s) => (s > 0 ? s - 1 : 0)), 1000);
    return () => clearInterval(iv);
  }, [codeCooldown]);

  const sendCode = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email: user.email });
      setCodeCooldown(data?.cooldown || 60);
      setCodeMode(true);
      setCode("");
      toast.success(t("code_sent"));
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(detail === "email_send_failed" ? t("email_send_failed") : (detail || t("something_wrong")));
    } finally {
      setBusy(false);
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    if (newPw.length < 6) {
      toast.error(t("password_min"));
      return;
    }
    if (newPw !== confirm) {
      toast.error(t("passwords_mismatch"));
      return;
    }
    setBusy(true);
    try {
      if (codeMode) {
        await api.post("/auth/reset-password", { email: user.email, code, new_password: newPw });
      } else {
        await api.post("/auth/change-password", { current_password: current, new_password: newPw });
      }
      toast.success(t("password_changed"));
      setCurrent(""); setNewPw(""); setConfirm(""); setCode(""); setCodeMode(false);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const known = ["invalid_code", "code_expired", "too_many_attempts", "no_reset_request"];
      if (known.includes(detail)) {
        toast.error(t(detail));
        if (detail !== "invalid_code") setCode("");
      } else {
        toast.error(err?.response?.status === 401 ? t("wrong_current_password") : (detail || "Failed"));
      }
    } finally {
      setBusy(false);
    }
  };

  const addPasskey = async () => {
    try {
      const optRes = await api.post("/auth/webauthn/register-options", {});
      const attestation = await startRegistration({ optionsJSON: optRes.data });
      const verify = await api.post("/auth/webauthn/register-verify", { credential: attestation });
      if (verify.data?.user) setUser(verify.data.user);
      try { localStorage.setItem("pmf_has_passkey", "1"); } catch (e) {}
      toast.success(t("passkey_added"));
    } catch (e) {
      // Device already has a passkey for this account (iOS error 1006) — success
      const msg = String(e?.message || e || "");
      if (e?.name === "InvalidStateError" || /1006|matchedexcluded/i.test(msg)) {
        try { localStorage.setItem("pmf_has_passkey", "1"); } catch (err) { /* ignore */ }
        toast.success(t("passkey_already_active"));
        return;
      }
      reportPasskeyError("profile-register", e);
      if (e?.name !== "NotAllowedError") toast.error(`${t("passkey_error")} — ${describePasskeyError(e)}`);
    }
  };

  return (
    <div className="flex-1 max-w-2xl mx-auto w-full px-4 sm:px-6 py-6">
      <div className="flex items-center gap-2 mb-5">
        <Button variant="ghost" size="icon" data-testid="profile-back" onClick={() => navigate(-1)} className="h-9 w-9">
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <h1 className="font-display font-bold text-2xl tracking-tight">{t("profile")}</h1>
      </div>

      {/* Identity card */}
      <Card className="p-5 flex items-center gap-4 mb-5" data-testid="profile-identity-card">
        <div className="relative shrink-0">
          <Avatar
            className={`h-16 w-16 ${user.avatar_media_id ? "cursor-zoom-in" : ""}`}
            onClick={() => user.avatar_media_id && setAvatarLightbox(true)}
            data-testid="profile-avatar"
          >
            {user.avatar_media_id && (
              <AvatarImage src={mediaSrcById(user.avatar_media_id)} alt={user.name} className="object-cover" />
            )}
            <AvatarFallback style={{ backgroundColor: user.avatar_color || "#64748b", color: "#fff" }} className="text-lg font-semibold">
              {initials(user.name)}
            </AvatarFallback>
          </Avatar>
          <button
            type="button"
            data-testid="profile-avatar-upload-btn"
            onClick={() => avatarFileRef.current?.click()}
            disabled={avatarBusy}
            aria-label={t("change_photo")}
            className="absolute -bottom-1 -right-1 h-7 w-7 rounded-full bg-primary text-primary-foreground flex items-center justify-center shadow-md border-2 border-background disabled:opacity-60"
          >
            {avatarBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />}
          </button>
          <input
            ref={avatarFileRef}
            type="file"
            accept="image/*"
            className="hidden"
            data-testid="profile-avatar-file-input"
            onChange={onPickAvatar}
          />
        </div>
        <div className="min-w-0">
          <div className="font-display font-semibold text-lg truncate" data-testid="profile-name">{user.name}</div>
          <div className="text-sm text-muted-foreground flex items-center gap-1.5 truncate" data-testid="profile-email">
            <Mail className="h-3.5 w-3.5 shrink-0" /> {user.email}
          </div>
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <Badge variant="secondary" className="text-[10px]">{user.title || t(user.role)}</Badge>
            {user.crew_id && (
              <Badge variant="outline" className="text-[10px] font-mono-plex uppercase">{user.crew_id.replace("crew-", "")}</Badge>
            )}
            {user.avatar_media_id && (
              <button
                type="button"
                data-testid="profile-avatar-remove-btn"
                onClick={removeAvatar}
                className="text-[11px] text-destructive underline underline-offset-2"
              >
                {t("remove_photo")}
              </button>
            )}
          </div>
        </div>
      </Card>

      {/* Personal connections (company card + partnerships moved to Admin → Business Cards) */}
      <PersonalConnectionsCard t={t} />

      {/* Passkey / FaceID */}
      <Card className="p-5 mb-5" data-testid="profile-passkey-card">
        <h2 className="font-display font-semibold text-base flex items-center gap-2 mb-1">
          <Fingerprint className="h-4 w-4 text-primary" /> FaceID / Passkey
        </h2>
        <p className="text-xs text-muted-foreground mb-3">
          {user.has_passkey ? t("passkey_active") : t("add_passkey")}
        </p>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" data-testid="profile-add-passkey" onClick={addPasskey}>
            <Fingerprint className="h-4 w-4 mr-1.5 text-primary" /> {t("add_passkey")}
          </Button>
          {user.has_passkey && (
            <Badge className="bg-[hsl(var(--success))] text-white text-[10px]" data-testid="profile-passkey-active">
              {t("passkey_active")}
            </Badge>
          )}
        </div>
      </Card>

      {/* Change password */}
      <Card className="p-5 mb-5" data-testid="profile-password-card">
        <h2 className="font-display font-semibold text-base flex items-center gap-2 mb-3">
          <KeyRound className="h-4 w-4 text-primary" /> {t("change_password")}
        </h2>
        <form onSubmit={changePassword} className="space-y-3">
          {!codeMode ? (
            <>
              <div className="space-y-1">
                <Label htmlFor="pw-current" className="text-xs">{t("current_password")}</Label>
                <Input id="pw-current" data-testid="profile-current-password" type="password" autoComplete="current-password" value={current} onChange={(e) => setCurrent(e.target.value)} />
              </div>
              <button
                type="button"
                data-testid="profile-forgot-current-btn"
                onClick={sendCode}
                disabled={busy}
                className="text-xs font-medium text-[#2B3990] hover:underline disabled:text-muted-foreground"
              >
                {t("forgot_current_password")}
              </button>
            </>
          ) : (
            <div className="space-y-2 rounded-lg border bg-muted/30 p-3" data-testid="profile-code-mode">
              <div className="flex items-center gap-2 text-xs font-medium text-[#2B3990]">
                <MailCheck className="h-3.5 w-3.5" /> {t("code_sent_to")} {user.email}
              </div>
              <div className="space-y-1">
                <Label className="text-xs">{t("email_code_label")}</Label>
                <InputOTP maxLength={6} value={code} onChange={setCode} inputMode="numeric" data-testid="profile-code-input">
                  <InputOTPGroup>
                    {[0, 1, 2, 3, 4, 5].map((i) => (
                      <InputOTPSlot key={i} index={i} className="h-10 w-9" />
                    ))}
                  </InputOTPGroup>
                </InputOTP>
              </div>
              <div className="flex items-center gap-4">
                <button
                  type="button"
                  data-testid="profile-resend-code-btn"
                  onClick={sendCode}
                  disabled={busy || codeCooldown > 0}
                  className="text-xs font-medium text-[#2B3990] hover:underline disabled:text-muted-foreground disabled:no-underline"
                >
                  {codeCooldown > 0 ? `${t("resend_in")} ${codeCooldown}s` : t("resend_code")}
                </button>
                <button
                  type="button"
                  data-testid="profile-use-current-btn"
                  onClick={() => { setCodeMode(false); setCode(""); }}
                  className="text-xs font-medium text-muted-foreground hover:text-foreground hover:underline"
                >
                  {t("use_current_password")}
                </button>
              </div>
            </div>
          )}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="space-y-1">
              <Label htmlFor="pw-new" className="text-xs">{t("new_password")}</Label>
              <Input id="pw-new" data-testid="profile-new-password" type="password" autoComplete="new-password" placeholder={t("password_min")} value={newPw} onChange={(e) => setNewPw(e.target.value)} />
            </div>
            <div className="space-y-1">
              <Label htmlFor="pw-confirm" className="text-xs">{t("confirm_password")}</Label>
              <Input id="pw-confirm" data-testid="profile-confirm-password" type="password" autoComplete="new-password" value={confirm} onChange={(e) => setConfirm(e.target.value)} />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="profile-change-password-submit"
            disabled={busy || !newPw || !confirm || (codeMode ? code.length !== 6 : !current)}
          >
            {busy ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <KeyRound className="h-4 w-4 mr-2" />}
            {t("change_password")}
          </Button>
        </form>
      </Card>

      <Button
        variant="outline"
        data-testid="profile-logout"
        onClick={() => { logout(); navigate("/"); }}
        className="w-full justify-center text-destructive border-destructive/30 hover:bg-destructive/5"
      >
        <LogOut className="h-4 w-4 mr-2" /> {t("sign_out")}
      </Button>

      {/* Danger zone — in-app account deletion (Apple Guideline 5.1.1) */}
      <Card className="p-5 border-destructive/30">
        <h3 className="font-display font-semibold text-sm text-destructive flex items-center gap-2">
          <Trash2 className="h-4 w-4" /> {t("delete_account")}
        </h3>
        <p className="text-xs text-muted-foreground mt-1.5">{t("delete_account_desc")}</p>
        <AlertDialog>
          <AlertDialogTrigger asChild>
            <Button
              variant="outline"
              data-testid="profile-delete-account"
              className="w-full justify-center mt-3 text-destructive border-destructive/40 hover:bg-destructive/5"
            >
              <Trash2 className="h-4 w-4 mr-2" /> {t("delete_account")}
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent data-testid="delete-account-dialog">
            <AlertDialogHeader>
              <AlertDialogTitle>{t("delete_account_confirm_title")}</AlertDialogTitle>
              <AlertDialogDescription>{t("delete_account_confirm_text")}</AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel data-testid="delete-account-cancel">{t("cancel")}</AlertDialogCancel>
              <AlertDialogAction
                data-testid="delete-account-confirm"
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                onClick={async () => {
                  setBusy(true);
                  try {
                    await api.delete("/auth/account");
                    toast.success(t("account_deleted"));
                    logout();
                    navigate("/");
                  } catch (err) {
                    toast.error(err?.response?.data?.detail || t("delete_account_failed"));
                  } finally {
                    setBusy(false);
                  }
                }}
              >
                {t("delete_account")}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </Card>

      {/* Circular crop: position/zoom the photo exactly as it will appear */}
      <AvatarLightbox src={avatarLightbox && user.avatar_media_id ? mediaSrcById(user.avatar_media_id) : null} name={user.name} onClose={() => setAvatarLightbox(false)} />
      <AvatarCropDialog
        open={!!cropFile}
        file={cropFile}
        onClose={() => setCropFile(null)}
        onCropped={uploadCroppedAvatar}
      />
    </div>
  );
}
